import React, { useState } from 'react';
import { getImageUrl } from 'src/utils/images';

export type EntityAvatarProps = {
  name: string;
  logo?: string | null;
  size?: number;
  /** Entities read as rounded squares; people as circles. */
  shape?: 'circle' | 'square';
};

/** Deterministic name -> palette slot so the same organization/restaurant always gets the
 *  same colour. A tiny hash over code points is plenty for five buckets — no need for a
 *  real hashing library. */
const PALETTE = [
  'shell-avatar--palette-purple',
  'shell-avatar--palette-teal',
  'shell-avatar--palette-cyan',
  'shell-avatar--palette-magenta',
  'shell-avatar--palette-blue',
];

const paletteFor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
};

/**
 * Organization and restaurant logos are object keys (e.g. `aB3dE6gH9jK2`), not URLs —
 * rendering one directly in an <img src> resolves it against the portal's own origin and
 * 404s (the same bug the Zalo export fixed server-side). This resolves the key through
 * `getImageUrl`, which returns the 256px thumb for a sized request, and falls back to a
 * generated initial avatar both when there is no logo and when a stale key 404s at runtime.
 */
export const EntityAvatar = ({ name, logo, size = 16, shape = 'circle' }: EntityAvatarProps) => {
  const [imageFailed, setImageFailed] = useState(false);
  const [renderedLogo, setRenderedLogo] = useState(logo);

  // A logo can change under the same mounted element (the trigger's mark tracks whichever
  // organization is current), so a previous failure must not stick forever. Reset during
  // render rather than in an effect: React's documented "adjusting state when a prop
  // changes" pattern. An effect would paint one frame carrying the previous logo's failure
  // before correcting itself, and it trips the repo's set-state-in-effect lint.
  if (logo !== renderedLogo) {
    setRenderedLogo(logo);
    setImageFailed(false);
  }

  const trimmedName = name.trim();
  const resolvedUrl = !imageFailed ? getImageUrl(logo ?? undefined, { width: size * 2, height: size * 2 }) : null;

  if (resolvedUrl) {
    return (
      <img
        className={['shell-avatar', `shell-avatar--${shape}`].join(' ')}
        style={{ width: size, height: size }}
        src={resolvedUrl}
        alt=""
        onError={() => setImageFailed(true)}
      />
    );
  }

  const initial = trimmedName ? trimmedName.charAt(0).toUpperCase() : '?';
  const paletteClass = trimmedName ? paletteFor(trimmedName) : 'shell-avatar--palette-neutral';

  return (
    <span
      className={['shell-avatar', `shell-avatar--${shape}`, 'shell-avatar--initial', paletteClass].join(' ')}
      style={{ width: size, height: size, fontSize: size * 0.625 }}
      aria-hidden
    >
      <span className="shell-avatar__glyph">{initial}</span>
    </span>
  );
};
